class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') return;
      if (this.skipNextEscapeKeyup) {
        this.skipNextEscapeKeyup = false;
        return;
      }
      this.close();
    });
    this.addEventListener('keydown', (event) => {
      const dialog = this.querySelector('[data-scntd-checkout-dialog]');
      if (event.code !== 'Escape' || !dialog?.open) return;

      event.preventDefault();
      event.stopPropagation();
      this.skipNextEscapeKeyup = true;
      this.closeCheckoutSuggestions(dialog);
    });
    this.addEventListener('click', this.handleCheckoutSuggestionsClick.bind(this));
    this.addEventListener('submit', this.handleCheckoutSubmit.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  handleCheckoutSubmit(event) {
    if (!event.submitter?.matches('[data-scntd-checkout-trigger]')) return;

    if (this.skipCheckoutSuggestions) {
      this.skipCheckoutSuggestions = false;
      return;
    }

    const dialog = this.querySelector('[data-scntd-checkout-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    event.preventDefault();
    dialog.showModal();
    dialog.querySelector('[data-scntd-suggestions-continue]')?.focus();
  }

  handleCheckoutSuggestionsClick(event) {
    const dialog = event.target.closest('[data-scntd-checkout-dialog]');
    if (!dialog) return;

    if (event.target.closest('[data-scntd-suggestions-close]')) {
      this.closeCheckoutSuggestions(dialog);
      return;
    }

    if (!event.target.closest('[data-scntd-suggestions-continue]')) return;

    const checkoutButton = this.querySelector('[data-scntd-checkout-trigger]');
    const cartForm = document.getElementById(checkoutButton?.getAttribute('form'));
    if (!checkoutButton || !cartForm) return;

    this.skipCheckoutSuggestions = true;
    dialog.close();
    cartForm.requestSubmit(checkoutButton);
  }

  closeCheckoutSuggestions(dialog) {
    dialog.close();
    this.querySelector('[data-scntd-checkout-trigger]')?.focus();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
